import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CrmLiveConnect } from "@/components/CrmLiveConnect";
import { CrmSyncDashboard } from "@/components/CrmSyncDashboard";
import { EnterpriseCapabilityPanel } from "@/components/EnterpriseCrm";

const COMING_SOON_ORDER = ["filevine", "litify", "casepeer", "smartadvocate", "smokeball", "filemaker"];
import {
  Plug, CheckCircle2, XCircle, Loader2, Plug2, RefreshCw, ScrollText, KeyRound, ShieldCheck, ChevronDown,
  Ticket, Copy, Download,
} from "lucide-react";

const AUTH_LABEL = { oauth2: "OAuth 2.0", api_key: "API Key" };

function FirmCodeCard() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => { try { const { data } = await api.get("/attorney/firm-code"); setData(data); } catch { /* ignore */ } };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { await api.post("/attorney/firm-code", { code: code || undefined }); toast.success("Firm code created"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard.writeText(data.link); toast.success("Link copied"); };
  const dl = () => { const a = document.createElement("a"); a.href = data.qr; a.download = `firm-${data.code}.png`; a.click(); };

  return (
    <div className="bg-navy rounded-3xl p-6 mb-8 text-white" data-testid="firm-code-card">
      <div className="flex items-center gap-2 mb-1"><Ticket className="w-5 h-5 text-cobalt-light" /><h2 className="font-heading font-bold text-lg">Your Firm Code</h2></div>
      <p className="text-sm text-slate-300">Clients who sign up with your firm code are auto-assigned to you and skip the public marketplace — you pay only the $50 access fee (per admin settings).</p>
      {data && data.code ? (
        <div className="mt-4 flex flex-col md:flex-row md:items-center gap-4">
          <div>
            <div className="font-heading text-3xl font-extrabold text-cobalt-light" data-testid="firm-code-value">{data.code}</div>
            <div className="mt-2 flex items-center gap-2">
              <input readOnly value={data.link} className="bg-white/10 rounded-full px-3 py-1.5 text-xs text-slate-200 w-64" />
              <button onClick={copy} data-testid="firm-copy-link" className="p-2 rounded-lg bg-white/10 hover:bg-white/20"><Copy className="w-4 h-4" /></button>
            </div>
            <div className="mt-2 text-xs text-slate-400">{data.clicks || 0} clicks · {data.signups || 0} sign-ups</div>
          </div>
          {data.qr && <div className="flex items-center gap-3"><img src={data.qr} alt="QR" className="h-24 w-24 rounded-xl bg-white p-1" data-testid="firm-qr" /><button onClick={dl} className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm"><Download className="w-4 h-4" /> QR</button></div>}
        </div>
      ) : data ? (
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="firm-code-input" placeholder="Custom code (e.g. KEITHLAW) or leave blank" className="flex-1 bg-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-slate-400 outline-none" />
          <button onClick={create} disabled={busy} data-testid="firm-code-create" className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all active:scale-95 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />} Create Firm Code</button>
        </div>
      ) : <div className="mt-4"><Loader2 className="w-5 h-5 animate-spin" /></div>}
    </div>
  );
}

function ConnectorCard({ c, onChange }) {
  const conn = c.connection || {};
  const connected = conn.status === "connected";
  const [busy, setBusy] = useState("");
  const [showCreds, setShowCreds] = useState(false);
  const [creds, setCreds] = useState("");
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState(null);

  const doConnect = async () => {
    setBusy("connect");
    try {
      await api.post(`/attorney/integrations/${c.key}/connect`, { credentials: creds ? { token: creds } : {} });
      toast.success(`${c.name} connected (sandbox)`);
      setShowCreds(false); setCreds(""); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const doDisconnect = async () => {
    setBusy("disconnect");
    try { await api.post(`/attorney/integrations/${c.key}/disconnect`); toast.success(`${c.name} disconnected`); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const doTest = async () => {
    setBusy("test");
    try { const { data } = await api.post(`/attorney/integrations/${c.key}/test`); toast.success(data.message || "Connection OK"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const loadLogs = async () => {
    setShowLogs((v) => !v);
    if (!showLogs) { try { const { data } = await api.get(`/attorney/integrations/${c.key}/logs`); setLogs(data); } catch { /* ignore */ } }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`connector-${c.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-navy flex items-center justify-center"><Plug2 className="w-5 h-5 text-white" /></div>
          <div>
            <div className="font-heading font-bold text-navy">{c.name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1.5"><KeyRound className="w-3 h-3" /> {AUTH_LABEL[c.auth_type]}
              {!c.live_supported && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600">SANDBOX</span>}
            </div>
          </div>
        </div>
        {connected
          ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-600 flex items-center gap-1" data-testid={`status-${c.key}`}><CheckCircle2 className="w-3.5 h-3.5" /> Connected</span>
          : <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 flex items-center gap-1" data-testid={`status-${c.key}`}><XCircle className="w-3.5 h-3.5" /> Not Connected</span>}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-500">
        <div>Last Sync: <span className="text-navy font-medium">{conn.last_sync_at ? new Date(conn.last_sync_at).toLocaleString() : "—"}</span></div>
        <div>Last Test: <span className="text-navy font-medium">{conn.last_test_at ? new Date(conn.last_test_at).toLocaleString() : "—"}</span></div>
      </div>
      {conn.last_error && <div className="mt-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">Error: {conn.last_error}</div>}

      {showCreds && !connected && (
        <div className="mt-4">
          <input value={creds} onChange={(e) => setCreds(e.target.value)} data-testid={`creds-${c.key}`}
            placeholder={c.auth_type === "oauth2" ? "OAuth token (optional in sandbox)" : "API key (optional in sandbox)"}
            className="w-full bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none" />
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {!connected ? (
          !showCreds ? (
            <button onClick={() => setShowCreds(true)} data-testid={`connect-${c.key}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover transition-all active:scale-95"><Plug className="w-4 h-4" /> Connect</button>
          ) : (
            <button onClick={doConnect} disabled={busy === "connect"} data-testid={`confirm-connect-${c.key}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover transition-all active:scale-95">{busy === "connect" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Confirm Connect</button>
          )
        ) : (
          <>
            <button onClick={doTest} disabled={busy === "test"} data-testid={`test-${c.key}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-all">{busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Test Connection</button>
            <button onClick={doConnect} data-testid={`reconnect-${c.key}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-all"><Plug className="w-4 h-4" /> Reconnect</button>
            <button onClick={doDisconnect} disabled={busy === "disconnect"} data-testid={`disconnect-${c.key}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 transition-all"><XCircle className="w-4 h-4" /> Disconnect</button>
          </>
        )}
        <button onClick={loadLogs} data-testid={`logs-${c.key}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-all"><ScrollText className="w-4 h-4" /> Sync Logs <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showLogs ? "rotate-180" : ""}`} /></button>
      </div>

      {showLogs && (
        <div className="mt-4 border-t border-slate-100 pt-3 max-h-56 overflow-y-auto space-y-1.5">
          {!logs?.logs?.length ? <p className="text-xs text-slate-400">No sync logs yet.</p> :
            logs.logs.map((l) => (
              <div key={l.log_id} className="text-xs flex items-start gap-2">
                <span className={`mt-0.5 h-1.5 w-1.5 rounded-full shrink-0 ${l.level === "error" ? "bg-red-500" : "bg-green-500"}`} />
                <div><span className={l.level === "error" ? "text-red-600" : "text-navy"}>{l.message}</span>
                  <span className="text-slate-400"> · {new Date(l.created_at).toLocaleString()}</span></div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export function Integrations() {
  const [data, setData] = useState(null);
  const load = async () => { try { const { data } = await api.get("/attorney/integrations"); setData(data); } catch { toast.error("Could not load integrations"); } };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  return (
    <div data-testid="integrations-page">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Attorney Settings</p>
      <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">Integration Center</h1>
      <p className="mt-2 text-slate-600 max-w-2xl">Connect your law firm CRM to import Crash Ready cases with no manual data entry. Each CRM is an independent connector — new platforms are added without changing the rest of the system.</p>
      <div className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-amber-700 bg-amber-50 rounded-full px-3 py-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Connectors run in sandbox mode until live vendor credentials are supplied.</div>

      <div className="mt-8"><FirmCodeCard /></div>

      <CrmLiveConnect />

      <CrmSyncDashboard />

      <div className="mt-12"><EnterpriseCapabilityPanel /></div>

      <div className="mt-12">
        <h2 className="font-heading text-lg font-bold text-navy" data-testid="coming-soon-heading">Coming Soon</h2>
        <p className="text-xs text-slate-500 mb-3">Additional CRMs — connect in sandbox today; production OAuth follows the Clio/PracticePanther pattern.</p>
        <div className="grid md:grid-cols-2 gap-5">
          {[...data.connectors]
            .filter((c) => !["clio", "practicepanther", "mycase", "needles"].includes(c.key))
            .sort((a, b) => {
              const ia = COMING_SOON_ORDER.indexOf(a.key), ib = COMING_SOON_ORDER.indexOf(b.key);
              return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
            })
            .map((c) => <ConnectorCard key={c.key} c={c} onChange={load} />)}
        </div>
      </div>

      <div className="mt-10">
        <h2 className="font-heading text-lg font-bold text-navy">On the roadmap</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {data.future.map((f) => <span key={f.key} className="text-xs font-medium px-3 py-1.5 rounded-full bg-slate-100 text-slate-500">{f.name}</span>)}
        </div>
      </div>
    </div>
  );
}
